import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  DEMO_PASSWORD,
  LOCAL_DEV_PASSWORD,
  normalizeEmail,
  safeNextPath,
} from "../domain/auth";
import { ErrorFlash } from "../components/ErrorFlash";
import { PasswordInput } from "../components/PasswordInput";
import {
  isLocalDevShortcutEmail,
  localDevShortcuts,
  localDevShortcutsForRole,
} from "../domain/devShortcuts";
import {
  localDevShortcutsEnabled,
  pathKeepingDebug,
  publicSupabaseConfig,
  runtimeFromBrowser,
} from "../domain/env";
import { browserSupabase } from "../domain/supabaseBrowser";
import { signInWithPassword } from "../domain/supabaseAuth";
import { useAuth } from "../state/AuthProvider";
import { useLocale } from "../state/i18n";
import { PrototypeBanner, PublicNav, SignOutButton } from "../components/ui";

export function SignIn() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { account, accounts, adoptHostedSession, pathForAccount, signIn } = useAuth();
  const { t } = useLocale();
  const role = params.get("role") === "staff" ? "staff" : "family";
  const resetDone = params.get("reset") === "1";
  const next = safeNextPath(params.get("next"));
  const search = `?${params.toString()}`;
  const runtime = runtimeFromBrowser();
  const showDevShortcuts = localDevShortcutsEnabled(runtime.name, search);
  const supabaseReady = publicSupabaseConfig() !== null;
  const devRows = showDevShortcuts
    ? localDevShortcuts().filter((item) =>
        role === "staff" ? item.role === "staff" : item.role !== "staff",
      )
    : [];

  const [email, setEmail] = useState(() => {
    if (!showDevShortcuts || role !== "staff") return "";
    return localDevShortcutsForRole("staff")[0]?.email ?? "";
  });
  const devPassword = supabaseReady ? LOCAL_DEV_PASSWORD : DEMO_PASSWORD;
  const [password, setPassword] = useState(
    showDevShortcuts && role === "staff" ? devPassword : "",
  );
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const normalized = normalizeEmail(email);
    const useLocalAuth =
      showDevShortcuts &&
      !supabaseReady &&
      (isLocalDevShortcutEmail(normalized) ||
        accounts.some(
          (item) =>
            item.email === normalized && item.password === DEMO_PASSWORD,
        ));
    if (supabaseReady && !useLocalAuth) {
      const hosted = await signInWithPassword(browserSupabase(), email, password);
      if (!hosted.ok) {
        setBusy(false);
        const devHint =
          showDevShortcuts && isLocalDevShortcutEmail(normalized)
            ? " Those dev rows are not in QA Auth yet — add the service role key to .env.local and run: npm run seed:qa-dev"
            : "";
        setError(hosted.error + devHint);
        return;
      }
      const continued = await adoptHostedSession();
      setBusy(false);
      if (!continued.ok) {
        setError(continued.error);
        return;
      }
      navigate(next ?? pathForAccount(continued.account));
      return;
    }
    const result = await signIn(email, password);
    setBusy(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    navigate(next ?? pathForAccount(result.account));
  }

  function fillDemo(demoEmail: string) {
    setEmail(demoEmail);
    setPassword(devPassword);
    setError("");
  }

  return (
    <div className="app">
      <PrototypeBanner />
      <main className="shell">
        <PublicNav />
        <div className="auth-panel">
          <p className="meta">
            {role === "staff" ? t("signin.staff") : t("signin.family")}
          </p>
          <h1>{t("signin.title")}</h1>
          <p className="lede">{t(supabaseReady ? "signin.ledeHosted" : "signin.lede")}</p>

          {resetDone ? (
            <div className="flash flash--success" role="status">
              {t("signin.resetDone")}
            </div>
          ) : null}

          {account ? (
            <div className="wire dashed">
              <div className="meta">{t("signin.already")}</div>
              <p style={{ margin: "0.45rem 0 0" }}>
                {account.given} {account.family} · {account.email}
              </p>
              <div className="actions">
                <Link className="btn" to={pathForAccount(account)}>
                  {t("signin.continue")}
                </Link>
                <SignOutButton className="ghost" />
              </div>
            </div>
          ) : null}

          <form className="stack" onSubmit={onSubmit}>
            <div className="field">
              <label htmlFor="email">{t("signin.email")}</label>
              <input
                id="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="password">{t("signin.password")}</label>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={setPassword}
                required
              />
            </div>
            {error ? <ErrorFlash key={error} message={error} /> : null}
            <div className="actions">
              <button type="submit" disabled={busy}>
                {busy ? t("signin.busy") : t("signin.submit")}
              </button>
              <Link className="btn ghost" to="/signup">
                {t("signin.create")}
              </Link>
            </div>
          </form>

          <p className="auth-links">
            <Link to="/forgot-password">{t("signin.forgot")}</Link>
            <Link
              to={pathKeepingDebug(
                "/signin",
                search,
                role === "staff" ? {} : { role: "staff" },
              )}
            >
              {role === "staff" ? t("signin.switchFamily") : t("signin.switchStaff")}
            </Link>
          </p>

          {showDevShortcuts ? (
          <div className="wire dashed" style={{ marginTop: "1.25rem" }}>
            <div className="meta">{t("signin.demos")}</div>
            <p className="anno" style={{ marginTop: "0.45rem" }}>
              {t(
                supabaseReady ? "signin.demosHintHosted" : "signin.demosHint",
                { password: devPassword },
              )}
            </p>
            <ul className="list-plain auth-demos">
              {devRows.map((item) => (
                <li key={item.email}>
                  <button
                    type="button"
                    className="ghost session-link"
                    onClick={() => fillDemo(item.email)}
                  >
                    <div className="row">
                      <div>
                        <div className="title">
                          <strong>
                            {item.given} {item.family}
                          </strong>
                        </div>
                        <div className="anno">{item.email}</div>
                      </div>
                      <div className="tag muted">
                        {item.role === "staff"
                          ? t("signin.tagStaff")
                          : item.role === "parent"
                            ? t("signin.tagParent")
                            : t("signin.tagStudent")}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
            {role === "family" ? (
              <p className="auth-links">
                <Link to={pathKeepingDebug("/signin/student", search)}>
                  {t("signin.browseStudents")}
                </Link>
              </p>
            ) : null}
          </div>
          ) : null}
        </div>
      </main>
    </div>
  );
}
